/**
 * HTML normalization utilities.
 *
 * After formatting operations, the DOM can accumulate redundant nested spans
 * and duplicate sibling elements. This module merges and flattens them.
 */

/** CSS properties allowed in merged inline styles. All others are stripped. */
const ALLOWED_CSS_PROPERTIES = new Set([
	"font-size",
	"font-family",
	"font-weight",
	"font-style",
	"color",
	"text-decoration",
	"line-height",
	"text-align",
	"background-color",
	"letter-spacing",
]);

/** Sanitize a CSS style string: keep only allowed properties, block dangerous values. */
const sanitizeStyle = (style: string): string => {
	return style
		.split(";")
		.map((decl) => decl.trim())
		.filter((decl) => {
			if (!decl) return false;
			const colonIdx = decl.indexOf(":");
			if (colonIdx === -1) return false;
			const prop = decl.substring(0, colonIdx).trim().toLowerCase();
			const value = decl.substring(colonIdx + 1).trim().toLowerCase();
			if (!ALLOWED_CSS_PROPERTIES.has(prop)) return false;
			if (
				value.includes("expression(") ||
				value.includes("javascript:") ||
				value.includes("behavior:")
			)
				return false;
			if (value.includes("url(")) return false;
			return true;
		})
		.join("; ");
};

const STORYDOC_SPECIAL_ATTRS = [
	"data-highlight-",
	"data-rkpi-",
	"data-text-animation-",
	// Variable pills are contenteditable=false atoms (already caught by the
	// check below); preserve them by their identifying attribute as well, so the
	// single-child span merge can never unwrap a pill and drop the token. Keep
	// in sync with VARIABLE_PILL_ATTR in shared/utils/variable-pill.
	"data-variable-name",
];

/** Check if an element is a Storydoc special element that should not be touched */
const isStorydocSpecialSpan = (el: Element): boolean => {
	if (el.getAttribute("contenteditable") === "false") return true;
	for (const attr of Array.from(el.attributes)) {
		if (STORYDOC_SPECIAL_ATTRS.some((prefix) => attr.name.startsWith(prefix))) {
			return true;
		}
	}
	return false;
};

/** Check if two elements have identical attributes */
const haveSameAttributes = (a: Element, b: Element): boolean => {
	if (a.attributes.length !== b.attributes.length) return false;
	for (const attr of Array.from(a.attributes)) {
		if (b.getAttribute(attr.name) !== attr.value) return false;
	}
	return true;
};

/**
 * Bottom-up: unwrap spans that have no attributes (they serve no purpose),
 * and merge single-child span styles upward into the parent span.
 */
const flattenRedundantSpans = (root: HTMLElement): void => {
	const spans = Array.from(root.querySelectorAll("span"));
	// Process deepest first (bottom-up)
	for (let i = spans.length - 1; i >= 0; i--) {
		const span = spans[i];
		if (!span.parentNode || !root.contains(span)) continue;
		if (isStorydocSpecialSpan(span)) continue;

		// Unwrap spans with no attributes
		if (span.attributes.length === 0) {
			span.replaceWith(...Array.from(span.childNodes));
			continue;
		}

		// Merge single-child span styles upward
		if (
			span.childNodes.length === 1 &&
			span.firstChild instanceof HTMLElement &&
			span.firstChild.tagName === "SPAN" &&
			!isStorydocSpecialSpan(span.firstChild)
		) {
			const child = span.firstChild;
			// Merge child's styles into parent (sanitized to prevent XSS via CSS)
			const childStyle = child.getAttribute("style") || "";
			const parentStyle = span.getAttribute("style") || "";
			if (childStyle) {
				const merged = sanitizeStyle(
					parentStyle ? `${parentStyle}; ${childStyle}` : childStyle,
				);
				if (merged) span.setAttribute("style", merged);
				else span.removeAttribute("style");
			}
			// Move child's children into parent
			while (child.firstChild) {
				span.insertBefore(child.firstChild, child);
			}
			child.remove();
		}
	}
};

/**
 * Merge adjacent sibling elements that have the same tag and attributes.
 */
const mergeAdjacentSiblings = (root: HTMLElement): void => {
	const tags = ["STRONG", "EM", "U", "S", "SUB", "SUP", "SPAN"];

	for (const tagName of tags) {
		const elements = Array.from(root.querySelectorAll(tagName.toLowerCase()));

		for (const el of elements) {
			if (!el.parentNode || !root.contains(el)) continue;
			if (isStorydocSpecialSpan(el)) continue;

			let sibling = el.nextSibling;
			while (
				sibling instanceof HTMLElement &&
				sibling.tagName === tagName &&
				!isStorydocSpecialSpan(sibling) &&
				haveSameAttributes(el, sibling)
			) {
				// Merge sibling's children into el
				while (sibling.firstChild) {
					el.appendChild(sibling.firstChild);
				}
				const next = sibling.nextSibling;
				sibling.remove();
				sibling = next;
			}
		}
	}
};

/**
 * Normalize inline formatting in a root element:
 * 1. Flatten redundant spans (bottom-up)
 * 2. Merge adjacent identical siblings
 * 3. Merge adjacent text nodes
 */
export const normalizeInlineFormatting = (root: HTMLElement): void => {
	flattenRedundantSpans(root);
	mergeAdjacentSiblings(root);
	root.normalize();
};
